/**
 * React Hook for Validated Forms
 * 
 * Combines Mantine useForm with AJV validation and GraphQL integration
 */

import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
    ValidationContext,
    ValidationResult
} from '../validation/validator';
import { validateField, validateWorkflowData } from '../validation/validator';

export interface ValidatedFormOptions<T> extends Record<string, any> {
    schemaName: any;
    validationContext?: ValidationContext;
    validateOnChange?: boolean;
    validateOnBlur?: boolean;
    showNotifications?: boolean;
    onValidationChange?: (result: ValidationResult) => void;
}

export interface ValidatedFormReturnType<T> extends ReturnType<typeof useForm> {
    validationResult: ValidationResult;
    isValidating: boolean;
    validateForm: () => Promise<ValidationResult>;
    validateSingleField: (path: string, value: any) => ValidationResult;
    getFieldError: (path: string) => string | null;
    getFieldWarning: (path: string) => string | null;
    isFieldValid: (path: string) => boolean;
    validationRules: string[];
    clearValidation: () => void;
}

export function useValidatedForm<T = Record<string, any>>(
    options: ValidatedFormOptions<T>
): ValidatedFormReturnType<T> {
    const {
        schemaName,
        validationContext,
        validateOnChange = true,
        validateOnBlur = true,
        showNotifications = false,
        onValidationChange,
        ...formOptions
    } = options;

    // State for validation
    const [validationResult, setValidationResult] = useState<ValidationResult>({
        valid: true,
        errors: []
    });
    const [isValidating, setIsValidating] = useState(false);

    // Initialize Mantine form with custom validation
    const form = useForm({
        ...formOptions,
        validate: (values: any) => {
            const result = validateWorkflowData(schemaName, values, validationContext);
            setValidationResult(result);

            // Convert validation errors to Mantine format
            const mantineErrors: Record<string, string> = {};
            result.errors.forEach(error => {
                mantineErrors[error.field] = error.message;
            });

            // Call custom validation change handler
            if (onValidationChange) {
                onValidationChange(result);
            }

            return mantineErrors;
        }
    });

    // Get validation rules for display
    const validationRules = useMemo(() => {
        try {
            return [];  // Placeholder - implement getValidationRules if needed
        } catch {
            return [];
        }
    }, [schemaName]);

    // Validate entire form
    const validateForm = useCallback(async (): Promise<ValidationResult> => {
        setIsValidating(true);

        try {
            const result = validateWorkflowData(
                schemaName,
                form.values,
                validationContext
            );

            setValidationResult(result);

            // Update form errors
            const mantineErrors: Record<string, string> = {};
            result.errors.forEach(error => {
                mantineErrors[error.field] = error.message;
            });
            form.setErrors(mantineErrors);

            // Show notifications for warnings if enabled
            if (showNotifications && result.warnings?.length) {
                result.warnings.forEach(warning => {
                    notifications.show({
                        title: 'Validation Warning',
                        message: `${warning.field}: ${warning.message}`,
                        color: 'yellow'
                    });
                });
            }

            // Show error notification if form is invalid
            if (showNotifications && !result.valid) {
                notifications.show({
                    title: 'Validation Failed',
                    message: `Please fix ${result.errors.length} validation error${result.errors.length > 1 ? 's' : ''}`,
                    color: 'red'
                });
            }

            if (onValidationChange) {
                onValidationChange(result);
            }

            return result;
        } finally {
            setIsValidating(false);
        }
    }, [schemaName, form.values, validationContext, form, showNotifications, onValidationChange]);

    // Validate single field
    const validateSingleField = useCallback((path: string, value: any): ValidationResult => {
        return validateField(schemaName, path, value);
    }, [schemaName]);

    // Get field error message
    const getFieldError = useCallback((path: string): string | null => {
        const error = validationResult.errors.find(err => err.field === path);
        return error?.message || null;
    }, [validationResult.errors]);

    // Get field warning message
    const getFieldWarning = useCallback((path: string): string | null => {
        const warning = validationResult.warnings?.find(warn => warn.field === path);
        return warning?.message || null;
    }, [validationResult.warnings]);

    // Check if field is valid
    const isFieldValid = useCallback((path: string): boolean => {
        return !validationResult.errors.some(err => err.field === path);
    }, [validationResult.errors]);

    // Clear validation state
    const clearValidation = useCallback(() => {
        setValidationResult({ valid: true, errors: [] });
        form.clearErrors();
    }, [form]);

    // Auto-validate on form changes if enabled
    useEffect(() => {
        if (validateOnChange && Object.keys(form.values).length > 0) {
            const timeoutId = setTimeout(() => {
                validateForm();
            }, 300); // Debounce validation

            return () => clearTimeout(timeoutId);
        }
        return undefined;
    }, [form.values, validateOnChange, validateForm]);

    // Enhanced form object with validation
    const enhancedForm: ValidatedFormReturnType<T> = {
        ...form,
        validationResult,
        isValidating,
        validateForm,
        validateSingleField,
        getFieldError,
        getFieldWarning,
        isFieldValid,
        validationRules,
        clearValidation,

        // Override setFieldValue to include validation
        setFieldValue: (path: string, value: any) => {
            form.setFieldValue(path as any, value);

            if (validateOnBlur) {
                const fieldResult = validateSingleField(path, value);
                if (!fieldResult.valid && fieldResult.errors.length > 0) {
                    form.setFieldError(path, fieldResult.errors[0]?.message ?? 'Validation error');
                } else {
                    form.clearFieldError(path);
                }
            }
        },

        // Override onSubmit to ensure validation
        onSubmit: (handleSubmit: (values: T, event?: React.FormEvent<HTMLFormElement>) => void) => {
            return async (event?: React.FormEvent<HTMLFormElement>) => {
                event?.preventDefault();

                const result = await validateForm();

                if (result.valid) {
                    handleSubmit(form.values, event);
                } else if (showNotifications) {
                    notifications.show({
                        title: 'Form Invalid',
                        message: 'Please fix validation errors before submitting',
                        color: 'red'
                    });
                }
            };
        }
    };

    return enhancedForm;
}

// Hook for real-time field validation
export function useFieldValidation(
    schemaName: any,
    fieldPath: string,
    value: any,
    enabled: boolean = true
) {
    const [fieldValidation, setFieldValidation] = useState<ValidationResult>({
        valid: true,
        errors: []
    });

    useEffect(() => {
        if (!enabled) return;

        const result = validateField(schemaName, fieldPath, value);
        setFieldValidation(result);
    }, [schemaName, fieldPath, value, enabled]);

    return {
        isValid: fieldValidation.valid,
        error: fieldValidation.errors[0]?.message || null,
        errors: fieldValidation.errors,
        hasError: fieldValidation.errors.length > 0
    };
}

// Hook for form submission with GraphQL integration
export function useValidatedFormSubmission<T>(
    schemaName: any,
    mutationHook: () => {
        mutate: (data: T) => Promise<any>;
        loading: boolean;
        error: Error | null;
    }
) {
    const { mutate, loading, error } = mutationHook();
    const [submissionResult, setSubmissionResult] = useState<{
        success: boolean;
        data?: any;
        error?: string;
    } | null>(null);

    const submitForm = useCallback(async (formData: T, validationContext?: ValidationContext) => {
        // First validate the data
        const validationResult = validateWorkflowData(schemaName, formData, validationContext);

        if (!validationResult.valid) {
            setSubmissionResult({
                success: false,
                error: `Validation failed: ${validationResult.errors.map(e => e.message).join(', ')}`
            });
            return { success: false, validationResult };
        }

        try {
            // Submit to GraphQL
            const result = await mutate(formData);

            setSubmissionResult({
                success: true,
                data: result
            });

            notifications.show({
                title: 'Success',
                message: 'Form submitted successfully',
                color: 'green'
            });

            return { success: true, data: result, validationResult };
        } catch (submitError) {
            const errorMessage = submitError instanceof Error ? submitError.message : 'Submission failed';

            setSubmissionResult({
                success: false,
                error: errorMessage
            });

            notifications.show({
                title: 'Submission Failed',
                message: errorMessage,
                color: 'red'
            });

            return { success: false, error: submitError, validationResult };
        }
    }, [schemaName, mutate]);

    return {
        submitForm,
        loading,
        error,
        submissionResult,
        clearSubmissionResult: () => setSubmissionResult(null)
    };
}